import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiGet, apiPost, getApiErrorMessage } from "@/api";
import { tokenQuery } from "./query-options";
import type { QueryKey } from "@tanstack/react-query";

export interface AffiliateConversion {
  id: string;
  convertedUserId: string;
  revenueCents: number;
  commissionCents: number;
  paid: boolean;
  currency: string;
  createdAt: string;
}

export interface AffiliateStatusData {
  enrolled: boolean;
  affiliateCode: string | null;
  commissionPercent?: number;
  payoutCurrency?: string;
  conversions: AffiliateConversion[];
  totalCommissionCents: number;
}

export interface AffiliatePayoutData {
  unpaidCents: number;
  paidCents: number;
  currency: string;
  minPayoutMinorUnits: number;
  canRequestPayout: boolean;
}

export function affiliateStatusQueryOptions(token: string | null) {
  return tokenQuery(["affiliate-status"], token, async (t) => {
    const res = await apiGet<AffiliateStatusData>("/affiliate/status", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Affiliate-Status konnte nicht geladen werden"));
    return {
      enrolled: res.data.enrolled,
      affiliateCode: res.data.affiliateCode,
      commissionPercent: res.data.commissionPercent,
      payoutCurrency: res.data.payoutCurrency,
      conversions: res.data.conversions ?? [],
      totalCommissionCents: res.data.totalCommissionCents ?? 0,
    } satisfies AffiliateStatusData;
  });
}

export function useAffiliateStatus() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(affiliateStatusQueryOptions(token));
}

export function affiliatePayoutsQueryOptions(token: string | null) {
  return tokenQuery(["affiliate-payouts"], token, async (t) => {
    const res = await apiGet<AffiliatePayoutData>("/affiliate/payouts", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Affiliate-Auszahlungen konnten nicht geladen werden"));
    return res.data;
  });
}

export function useAffiliatePayouts() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(affiliatePayoutsQueryOptions(token));
}

/**
 * Enrolling changes both the status and the payout figures, so both are cleared. The keys are
 * taken from the query factories rather than written out again: a literal that drifts from the
 * query it is meant to clear leaves the OLD answer on screen — here, "not enrolled" to a user
 * who just enrolled — and nothing fails.
 */
export function enrollAffiliateInvalidateKeys(token: string | null): readonly QueryKey[] {
  return [affiliateStatusQueryOptions(token).queryKey, affiliatePayoutsQueryOptions(token).queryKey];
}

export function useEnrollAffiliate() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiPost("/affiliate/enroll", token, {});
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Affiliate-Aktivierung fehlgeschlagen"));
      return res.data;
    },
    onSuccess: () => {
      for (const queryKey of enrollAffiliateInvalidateKeys(token)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
